/**
 * Format a total time in seconds to a human-readable string.
 * @param {number} seconds
 * @returns {string|null}
 */
export const formatTotalTime = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h${m > 0 ? `${String(m).padStart(2, '0')}` : ''}`;
    if (m === 0) return '<1min';
    return `${m}min`;
};

/**
 * Get the localized title for a course.
 * @param {object} course
 * @param {string} language - 'fr' or 'en'
 * @returns {string}
 */
export const getCourseTitle = (course, language) => {
    return language === 'fr'
        ? (course.libelle_fr || course.titreCourse || course.titre || 'Sans titre')
        : (course.libelle_en || course.titreCourse || course.titre || 'Untitled');
};

/**
 * Determine if a course is a quiz type.
 * @param {object} course
 * @returns {boolean}
 */
export const getCourseType = (course) => {
    const type = (course.typeCourse || course.type || '').toUpperCase();
    return course.hasQuiz || type === 'QUIZ' || type === 'QUIZZ';
};

/**
 * Calculate average progression for a group of courses.
 * @param {Array} coursesInGroup
 * @returns {number}
 */
export const getGroupProgress = (coursesInGroup) => {
    if (!coursesInGroup || coursesInGroup.length === 0) return 0;
    const totalProgress = coursesInGroup.reduce((sum, course) => sum + (course.progress || 0), 0);
    return Math.round(totalProgress / coursesInGroup.length);
};

/**
 * Calculate separate progression for courses (reading) and quizzes within a group.
 * @param {Array} coursesInGroup
 * @returns {{ courseProgress: number|null, quizProgress: number|null, courseCount: number, quizCount: number }}
 */
export const getGroupProgressSplit = (coursesInGroup) => {
    if (!coursesInGroup || coursesInGroup.length === 0) {
        return { courseProgress: null, quizProgress: null, courseCount: 0, quizCount: 0 };
    }

    const reading = [];
    const quizzes = [];

    coursesInGroup.forEach(course => {
        if (getCourseType(course)) {
            quizzes.push(course);
        } else {
            reading.push(course);
        }
    });

    const courseProgress = reading.length > 0
        ? Math.round(reading.reduce((sum, c) => sum + (c.progress || 0), 0) / reading.length)
        : null;

    const quizProgress = quizzes.length > 0
        ? Math.round(quizzes.reduce((sum, c) => sum + (c.progress || 0), 0) / quizzes.length)
        : null;

    return { courseProgress, quizProgress, courseCount: reading.length, quizCount: quizzes.length };
};

/**
 * Calculate total time for a group of courses.
 * @param {Array} coursesInGroup
 * @returns {number}
 */
export const getGroupTotalTime = (coursesInGroup) => {
    if (!coursesInGroup || coursesInGroup.length === 0) return 0;
    return coursesInGroup.reduce((sum, course) => sum + (course.totalTime || 0), 0);
};
